import type {
  CharacterIndexFigureKind,
  CharacterIndexInput,
  CharacterIndexMentionType,
  CharacterIndexSourceAnchor
} from "./types.js";
import {
  mapValidatedWindowRangeToSourceAnchors,
  prepareSingleWitnessCharacterExtractionCall,
  validatePreparedExtractionCall,
  type CharacterExtractionScope,
  type CharacterExtractionSourceMapSpan,
  type PreparedCharacterExtractionScopeMetadata,
  type PreparedCharacterExtractionValidationIssue
} from "./extractionPreparation.js";
import {
  prepareSingleWitnessCoreferenceChainExtractionWindowCalls,
  type CoreferenceChainWindowMetadata
} from "./coreferenceChainWindowing.js";
import type { CharacterMentionTextRange } from "./mentionGrounding.js";

export const SINGLE_WITNESS_COREFERENCE_CHAIN_EXTRACTION_V2_PROMPT_VERSION =
  "single-witness-coreference-chain-extraction-v2";

const COREFERENCE_CHAIN_V2_PROMPT_PREFIX = [
  "# Coreference-chain extraction v2 — classical Arabic witness (full rule set)",
  "",
  "## Goal",
  "For each figure in the text, build its **coreference chain**: the textually ordered list of **every overt expression that refers to that figure**, each anchored to a verbatim context. One chain per figure. Membership in a chain *is* the coreference claim.",
  "",
  "## Scope and discipline (unchanged, non-negotiable)",
  "- **Reference resolution only.** No narrative-unit selection, no aboutness tests, no figure typology (major/minor, stable/variant). Those are downstream.",
  "- **Single, self-contained witness.** Use this text's words only; no knowledge of the wider work, other manuscripts, or the tradition.",
  "- **No canonicalization.** Record the witness's own expressions verbatim; never relabel a figure to a known name (no زعيم السباع → كليلة).",
  "- **Verbatim always.** Every `mention` and `context` is copied exactly, including the witness's own orthographic quirks.",
  "- **Under-merge.** If two expressions might or might not denote the same figure, keep separate chains.",
  "- **Never guess a pronoun.** An unresolvable mention goes to `unassigned_mentions` (see Output) — it is recorded, not silently dropped.",
  "- Ignore the truncation marker `[...]`.",
  "",
  "---",
  "",
  "## 1. What counts as a figure (chain-bearing entity)",
  "",
  "**Include:**",
  "- **Persons** — named or unnamed (التاجر، الجارية، أحدهم).",
  "- **Agentive non-humans** — animals, jinn, and other beings that act, speak, or are spoken to as participants (الغراب، الجرذ، المطوّقة، السلحفاة). *Default ON* — in fable and frame-tale corpora these are the cast. [Toggle OFF only for corpora where animals are scenery.]",
  "- **Collectives acting as a character** — a group that recurs as a participant (الحمام، القوم، اللصوص، الجواري). A recurring collective gets **its own chain** (kind `group`); see §5 on plural mentions.",
  "",
  "**Exclude:**",
  "- **Placeholders** فلان / فلانة / بنو فلان — never an anchor, never a mention. (A real pronoun in the same clause still belongs to its real figure's chain.)",
  "- **Place names** and other locations (قرنونا، مطورة).",
  "- **Non-agentive entities** — objects, abstractions, the body parts of a figure (رأسه: the ـه is a mention of the figure; رأس is not a figure).",
  "- **Invoked deity in formulas** — والله، إن شاء الله، الحمد لله: formulaic invocation is not a story participant. [Toggle ON only if God acts as a participant in the narrative.]",
  "- **Generic / gnomic referents** — see §4-G. The العاقل، المرء، الإنسان، مَن فعل كذا of morals and maxims denote no story figure.",
  "",
  "---",
  "",
  "## 2. Mention inventory — nominal layer",
  "",
  "### 2a. Proper names",
  "ظهره، دمنة، شتربة — including names introduced by **naming formulas**: اسمه X، يقال له X، يسمّى X، يُدعى X. The name in the formula is a mention of the figure (واسمي ظهره → ظهره joins the speaker's chain).",
  "",
  "### 2b. Definite descriptions and epithets",
  "Role, rank, profession, kinship, attribute: الملك، الوزير، ابن الملك، التاجر، الأكّار، الغلام، الفتى، المرأة الشريفة، صاحب الجرذ.",
  "- **ال العهدية (anaphoric definite article)** is the cardinal nominal-anaphora device of Arabic: an indefinite introduction (كان رجلٌ تاجرٌ…) is later resumed by the definite (فقام الرجلُ / التاجرُ). The definite resumption links to the indefinite introducer's chain. The **indefinite first mention is a valid anchor**.",
  "- **Role nouns rebind by narrative state, not lexeme.** الملك before and after a succession, الوزير after a replacement, denote different figures. Track who currently holds the role; never chain by matching the string الملك.",
  "",
  "### 2c. Maximal span and apposition",
  "A title + name or name + apposition is **one mention with the maximal span**: الملكُ دبشليم، دمنةُ الوزيرُ → one chain entry each, copied whole.",
  "",
  "### 2d. Construct state (إضافة) — nested mentions",
  "A construct can refer **twice**: ابنُ الملكِ is one mention of the son **and** contains a nested mention الملك of the king — extract **both**, each into its own chain. Likewise زوجةُ التاجر (wife + merchant), غلامُ ابنِ الملكِ (three figures, three mentions). Kinship nouns with suffixes do the same: أبوه = a mention of the father (host أبو) **and** the ـه is a mention of the son — two chain entries from one token. Match the five nouns across case (أبوه / أباه / أبيه = same expression).",
  "",
  "### 2e. Quantified partitives and emphatics",
  "- أحدُهم، بعضُهم، إحداهنّ، كلاهما، كلتاهما، كلُّهم: the **bound pronoun** (ـهم، ـهما، ـهنّ) is a mention of the **group/set**; the whole phrase may introduce or denote an **individual or subset** with its own chain (أحدُهم من أبناء الملوك anchors a new figure).",
  "- Emphatic نفسه / عينه / كلّه: the suffix is the mention; نفس/عين/كل is التوكيد, not a separate referent.",
  "",
  "### 2f. Vocatives (المنادى) — addressee mentions",
  "يا + NP is an overt mention of the **addressee**: يا زيرك، يا أيّها الملك، يا ابنَ أخي، أيا غلام. Particles: يا، أيا، هيا، أيْ، أ، and يا أيّها / يا أيّتها + definite.",
  "- A construct vocative nests like §2d: يا ابنَ الملك → addressee mention + nested الملك mention.",
  "- Kinship vocatives with the compensatory tāʾ or 1st-person yāʾ refer **twice**: يا أبتِ / يا بُنيّ = a mention of the addressee (father / son) whose ت / ـيّ encodes the **speaker's** \"my\" — add a 1st-person entry to the speaker's chain too.",
  "",
  "---",
  "",
  "## 3. Mention inventory — pronominal layers",
  "Arabic grammar splits pronouns into **overt** (بارز: free or bound) and **covert** (مستتر). The include/exclude boundary runs on that native split — *everything overt is in by default; only the covert layer is a toggle.*",
  "",
  "### Layer P1 — free (independent) pronouns. Always include.",
  "- **Nominative set (full paradigm):** أنا، نحن، أنتَ، أنتِ، أنتما، أنتم، أنتنّ، هو، هي، هما، هم، هنّ.",
  "- **Accusative إيّا-set:** إيّاي، إيّانا، إيّاكَ، إيّاكِ، إيّاكما، إيّاكم، إيّاكنّ، إيّاه، إيّاها، إيّاهما، إيّاهم، إيّاهنّ.",
  "- **ضمير الفصل** (هو in نحو: كان زيدٌ هو القائمَ) corefers with the subject — include it in that figure's chain.",
  "",
  "### Layer P2 — bound accusative/genitive suffixes (ضمائر النصب والجرّ المتصلة). Always include.",
  "ـي / ـني (with نون الوقاية)، ـنا، ـكَ، ـكِ، ـكما، ـكم، ـكنّ، ـه، ـها، ـهما، ـهم، ـهنّ — on verbs (رأيتُه، أرسلتني), nouns (حسنُها، أخوه), and prepositions (إليه، له، بها، معه، عنده، عليهنّ).",
  "- Record the **host token verbatim** as `mention` (e.g., إليه، أرسلتني); the suffix is the referring element.",
  "- Vowel-harmony variants are identical: ـهُ / ـهِ، ـهم / ـهِم.",
  "",
  "### Layer P3 — bound nominative subject suffixes (ضمائر الرفع المتصلة). Include by default.",
  "These are **overt written pronouns**, not zero subjects:",
  "- **تاء الفاعل:** قلتُ، قلتَ، قلتِ، قلتما، قلتم، قلتنّ",
  "- **نا الفاعلين:** قلنا، كنّا",
  "- **واو الجماعة:** قالوا، يقولون، قولوا",
  "- **ألف الاثنين:** قالا، يقولان، قولا",
  "- **نون النسوة:** قلنَ، يقلنَ، قلنَ (imper.)",
  "- **ياء المخاطبة:** تقولين، قولي",
  "",
  "Record the **verb token verbatim** as `mention` (كنتُ، قالوا); the subject suffix is the referring element. [Toggle OFF only to reproduce v1 behavior — note that turning this off silently deletes most first- and second-person reference in narration: كنتُ وُلّيتُ عهدَ أبي carries two mentions of the speaker.]",
  "",
  "**Not pronouns — never extract as mentions:**",
  "- **تاء التأنيث الساكنة** (قالتْ، ذهبتْ) — a gender-agreement marker; the subject of قالتْ is covert (Layer P4). It is, however, **resolution evidence** (§6).",
  "- نون التوكيد (لتفعلنّ), tanwīn, نون الأفعال الخمسة (the ن of يفعلان/يفعلون is a mood marker; the pronoun there is the ا / و).",
  "",
  "**Unvocalized-text traps (resolve from context, never from the bare string):**",
  "- قلت = قلتُ / قلتَ / قلتِ — person decided by the speech situation (§6).",
  "- كتبنا = كتبْنا \"we wrote\" (subject نا) vs. كتبَنا \"he wrote to us\" (object نا) — decided by syntax and context.",
  "- قالت final ت (agreement, sākin) vs. قلتَ/قلتُ medial-root + ت الفاعل — parse the verb before classifying the ت.",
  "- تقول = \"she says\" or \"you (m.) say\" — the mustatir is ambiguous between هي and أنتَ; dialogue context decides.",
  "",
  "### Layer P4 — covert subjects (الضمير المستتر). Toggle, default OFF.",
  "The truly zero layer: 3rd-person perfects (قال هو، قالتْ هي), imperfects with hidden subject (يقول، تقول، أقول، نقول), and the hidden أنتَ of imperatives (اذهبْ، اسمعْ). If ON, represent each by the **governing verb verbatim**. This is the largest and least reliable layer; turn it on only for exhaustive pro-drop coverage.",
  "",
  "---",
  "",
  "## 4. Mention inventory — deictics, relatives, and exclusions",
  "",
  "### 4a. Referential demonstratives — full paradigm",
  "هذا، هذه، هذان/هذين، هاتان/هاتين، هؤلاء، ذاك، ذلك، تلك، ذانك، تانك، أولئك — both **determiner use** (ذلك الغلامُ، تلك المرأة: record the full phrase) and **pronominal use** denoting a person (هذا الذي أخبرتُك عنه: record هذا).",
  "",
  "### 4b. Demonstrative exclusions — discourse, event, and place deixis",
  "ذلك pointing at a **proposition, event, or stretch of discourse** is not a person mention: ذلك أنّ…، لذلك، كذلك، مع ذلك، بعد ذلك، عند ذلك، فلمّا كان ذلك، ففعلنَ ذلك. Place deixis is excluded: هنا، هناك، ثَمّ.",
  "",
  "### 4c. Relative pronouns — full paradigm",
  "الذي، التي، اللذان/اللذين، اللتان/اللتين، الذين، الألى، اللاتي/اللواتي/اللائي — when the relative phrase denotes a figure (الذي هو من أبناء الملوك). **Free relatives:** مَن used referentially of a person (فقال مَن أنت؟ is interrogative — exclude; جاء مَن أرسلتَه — include). ما is non-person; include only if its referent is an included non-human agent. أيّ + suffix (أيُّهم) follows §2e.",
  "",
  "### 4d. الضمير العائد (resumptive pronoun)",
  "The resumptive inside a relative clause corefers with the relative head — extract **both**: in الغلامُ الذي رأيتُه, the chain of the boy gets الغلام الذي… and رأيتُه (host, ـه), and the speaker's chain gets رأيتُه (تُ, Layer P3).",
  "",
  "### 4e. ضمير الشأن / ضمير القصة — exclude",
  "The anticipatory ـه/ـها expounded by a following clause refers to a **situation**, not a person: زعموا أنّه كان غرابٌ…، فإنّها لا تعمى الأبصار. Tale-opening زعموا أنّه كان… is the canonical case — the ـه there is never a figure mention. (The زعموا itself is generic \"they\"; exclude per §4-G.)",
  "",
  "### 4f. Predication boundary",
  "Predicate nominals, ḥāl, and tamyīz are **attributes, not mentions**: in أنا ابنُ الملكِ, the speaker's chain gets أنا only — ابن الملك is predicative — but its **nested** الملك is still a mention of the king (§2d). In كان وزيرًا, وزيرًا is not a mention. Exception: names inside naming formulas are mentions (§2a). [Toggle: record predicates as `attribute_of` entries if a downstream consumer wants them.]",
  "",
  "### 4g. Generic and arbitrary reference — exclude",
  "Gnomic morals and maxims (الحكمة closing or interrupting a tale) use generic NPs and pronouns: العاقل، المرء، الإنسان، الصديق، مَن صنع كذا كان كذا، and every pronoun bound inside such a generalization (إنّ العاقلَ مَن إذا نزلت به النازلة لم يدهش — none of these enter any chain). Test: could the clause stand as a proverb outside this story? Then its referents are generic. Impersonal generic plurals likewise: زعموا، قالوا (gnomic \"people say\"), قيل.",
  "",
  "---",
  "",
  "## 5. Plural, dual, and split reference",
  "A dual or plural mention may denote a **set of tracked figures** (هما = the crow and the rat) or a **collective figure** (الحمامُ، هنّ = the doves).",
  "",
  "- **Set of individually tracked figures:** the mention is **duplicated into each member's chain**, identical `mention`/`context`, with `with` listing the co-referent figure ids. (Overlap across chains is expected and legible — same convention as grounding overlap in the dedup prompt.)",
  "- **Recurring collective:** the collective owns a chain (kind `group`); its plural pronouns (هنّ، ـهنّ، نون النسوة forms like فقلنَ، تتخاذلن) chain to the group figure, not to invented members. If a member is individuated (المطوّقة out of the doves), the individual gets her own chain; mentions of \"the rest\" (صواحباتها، سائرهنّ) stay with the group.",
  "- نحن = current speaker + others: enters the speaker's chain with `with` listing the other members if recoverable, else the group chain.",
  "",
  "---",
  "",
  "## 6. Resolution procedure and evidence",
  "Link by **figure-in-context, never by surface string**. Work in two passes: **(1) harvest** every candidate mention by the closed inventories above, in textual order; **(2) cluster** them into chains using the evidence below.",
  "",
  "**Deictic engine for speech (frame tales nest deeply):**",
  "- Maintain a **speaker stack**. Every speech verb (قال، قالت، أجاب، نادى، حدّث، زعم + قول complements) pushes a (speaker → addressee) frame; the quote's end pops it.",
  "- Inside a quote: **1st person = current innermost speaker; 2nd person = current addressee(s)**. A tale told inside a tale shifts the deictic center again — resolve أنا/أنتَ/تُ/ـك against the innermost frame, not the outer narrator.",
  "- **Bare-قال turn alternation:** in a two-party dialogue, unmarked قال / قالت / فقال alternates between the two interlocutors unless gender, content, or an explicit subject blocks it.",
  "- A frame narrator (the حدّثني / قال الراوي voice) is itself a figure with a chain if it is referred to.",
  "",
  "**Morphological agreement (use even when the marker is not a mention):**",
  "- Gender: قالت، فعلتْ → female referent; تاء التأنيث filters candidates.",
  "- Number incl. **dual**: قالا، ـهما demand a two-member referent.",
  "- Rationality: non-rational plurals normally take feminine-singular agreement (الأيامُ تمضي… هي); **personified animals in fables take rational agreement** (الحمامُ قلنَ / فعلنَ) — treat rational agreement on animals as evidence of figure status, not an error.",
  "",
  "**Direction and locality:**",
  "- Classical prose is anaphoric: **resolve backward** to the nearest *compatible* antecedent (الضمير يعود على أقرب مذكور صالح). Cataphora is essentially limited to ضمير الشأن and a few fixed constructions — never posit it in narration.",
  "- Connectives carry subject-continuity signal: فـ tends to preserve the current agent; ثمّ and sentence-initial و are weaker; an overt new subject or a قال frame resets it.",
  "- Agreement, role-state (§2b), and story logic **override** mere proximity.",
  "",
  "**Discipline:** under-merge between chains; within a chain, a mention enters only if the evidence converges. Otherwise → `unassigned_mentions` with the candidate figure ids.",
  "",
  "---",
  "",
  "## 7. Orthography and matching (premodern witnesses)",
  "Identity matching is performed **across** orthographic variance; output stays verbatim.",
  "- Treat as equivalent for matching: hamza forms (ا/أ/إ/آ، ؤ، ئ), ى/ي, ة/ه, defective vs. plene spellings (هرون/هارون، الرحمن/الرحمان), presence/absence of taškīl and šadda, case-ending variation (أبوه/أباه/أبيه؛ عمرٌو/عمرًا).",
  "- **Proclitics** و، ف، ب، ك، ل، الـ attach to the mention's token: record the full orthographic token verbatim (فالغلامُ، وللملكِ، أفأنتَ) — the referring core is the noun/pronoun inside it.",
  "- Same-figure spelling variants stay in **one** chain, each recorded verbatim; never normalize the output.",
  "",
  "---",
  "",
  "## 8. Output",
  "Return **JSON only** — no markdown fences, no preamble, no trailing text.",
  "",
  "```json",
  "{",
  "  \"figures\": [",
  "    {",
  "      \"id\": \"F1\",",
  "      \"kind\": \"person\",",
  "      \"anchor\": { \"name\": \"<first nominal mention, verbatim>\", \"transliteration\": \"<IJMES>\" },",
  "      \"chain\": [",
  "        {",
  "          \"id\": \"F1.m1\",",
  "          \"mention\": \"<verbatim referring expression or host token>\",",
  "          \"context\": \"<verbatim clause locating it>\",",
  "          \"with\": [\"F2\"]",
  "        }",
  "      ]",
  "    }",
  "  ],",
  "  \"unassigned_mentions\": [",
  "    { \"mention\": \"<verbatim>\", \"context\": \"<verbatim>\", \"candidates\": [\"F1\", \"F3\"] }",
  "  ]",
  "}",
  "```",
  "",
  "- `chain` is ordered by textual appearance. Every entry carries a verbatim `context` long enough to make the referent recoverable (mandatory — a bare ـه or أنا is otherwise uninterpretable).",
  "- `id`s are stable handles for downstream relationship-edge extraction; numbering is by order of first appearance.",
  "- `kind` ∈ {person, animal, being, group} — a grammatical-ontological tag only (it governs §1/§5 behavior). It is **not** the forbidden interpretive typology; no stable/variant/major/minor labels exist anywhere in the output.",
  "- `with` appears only on set-denoting mentions (§5). Omit when singular.",
  "- **Anchor** = first non-placeholder **nominal** mention, verbatim + IJMES. A figure needs at least one nominal expression to bear a chain; pronoun-only figures are not invented [toggle as before].",
  "- Defaults: Layer P3 ON · Layer P4 OFF · vocatives ON · nested construct mentions ON · agentive non-humans ON · predicate `attribute_of` OFF · per-mention transliteration OFF · per-mention grammatical type tags OFF [both available as toggles].",
  "",
  "## 9. Worked micro-examples",
  "",
  "**P3 + naming formula + nesting (the protagonist's chain, corrected):**",
  "فقال الفتى أنا ابنُ فلانٍ الملكِ ملكِ قرنونا واسمي ظهره كنتُ وُلّيتُ عهدَ أبي",
  "- Protagonist's chain: الفتى → أنا → ظهره (naming formula) → كنتُ (تُ, P3) → وُلّيتُ (تُ, P3) → أبي (ـي, P2).",
  "- ابن فلان الملك is predicative (§4f) and فلان a placeholder — neither is a mention of him; but أبي adds a **nested** mention of the father figure (host أب) to the father's chain (§2d). قرنونا is a place — excluded.",
  "",
  "**Vocative + dual + speech stack:**",
  "فنادته يا زيرك فقال مَن أنتِ قالت أنا أختُك المطوّقة",
  "- فنادته: ـه → the rat's chain; covert \"she\" subject is P4 (off by default).",
  "- يا زيرك: vocative → the rat's chain.",
  "- أنتِ: 2nd person, feminine, inside his speech → the dove's chain. مَن is interrogative — excluded.",
  "- Bare قالت alternates the turn (§6): أنا → the dove; أختُك → predicative, excluded, but its ـك is a nested mention → the rat's chain; المطوّقة → apposition to the predicate naming her — naming-formula treatment, her chain.",
  "",
  "**Gnomic exclusion + ضمير الشأن:**",
  "قال الفيلسوف زعموا أنّه كان غرابٌ… فإنّ العاقلَ لا يتّكل على قوّته",
  "- الفيلسوف → his chain. زعموا generic (§4g). أنّه = ضمير الشأن (§4e) — excluded. غرابٌ — indefinite introduction, anchors the crow's chain (§2b). العاقل and everything bound to it — gnomic, excluded.",
  "",
  "---",
  "",
  "TEXT"
].join("\n");

export type CoreferenceChainV2PromptTextBuilder = (sourceText: string) => string;

export interface PrepareSingleWitnessCoreferenceChainV2ExtractionOptions {
  readonly promptVersion?: string;
  readonly promptTextBuilder?: CoreferenceChainV2PromptTextBuilder;
}

export interface PrepareSingleWitnessCoreferenceChainV2WindowOptions
  extends PrepareSingleWitnessCoreferenceChainV2ExtractionOptions {
  readonly maxRowsPerWindow?: number;
  readonly overlapRows?: number;
}

export interface PreparedSingleWitnessCoreferenceChainV2ExtractionCall {
  readonly promptText: string;
  readonly sourceText: string;
  readonly sourceMap: readonly CharacterExtractionSourceMapSpan[];
  readonly scope: PreparedCharacterExtractionScopeMetadata;
  readonly promptVersion: string;
}

export interface PreparedSingleWitnessCoreferenceChainV2WindowCall
  extends PreparedSingleWitnessCoreferenceChainV2ExtractionCall {
  readonly window: CoreferenceChainWindowMetadata;
}

export type CoreferenceChainV2FigureKind = CharacterIndexFigureKind;

export interface SingleWitnessCoreferenceChainV2Output {
  readonly figures: readonly SingleWitnessCoreferenceChainV2Figure[];
  readonly unassigned_mentions: readonly SingleWitnessCoreferenceChainV2UnassignedMention[];
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainV2Figure {
  readonly id: string;
  readonly kind: CoreferenceChainV2FigureKind;
  readonly anchor: SingleWitnessCoreferenceChainV2Anchor;
  readonly chain: readonly SingleWitnessCoreferenceChainV2Mention[];
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainV2Anchor {
  readonly name: string;
  readonly transliteration: string;
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainV2Mention {
  readonly id: string;
  readonly mention: string;
  readonly context: string;
  readonly with?: readonly string[];
  readonly [key: string]: unknown;
}

export interface SingleWitnessCoreferenceChainV2UnassignedMention {
  readonly mention: string;
  readonly context: string;
  readonly candidates?: readonly string[];
  readonly [key: string]: unknown;
}

export type SingleWitnessCoreferenceChainV2ValidationIssueCode =
  | "invalid-output"
  | "invalid-figures"
  | "invalid-figure"
  | "empty-figure-id"
  | "invalid-kind"
  | "invalid-anchor"
  | "empty-anchor-name"
  | "empty-anchor-transliteration"
  | "invalid-chain"
  | "invalid-chain-mention"
  | "empty-chain-mention-id"
  | "empty-mention"
  | "empty-context"
  | "invalid-with"
  | "invalid-unassigned-mentions"
  | "invalid-unassigned-mention"
  | "invalid-candidates";

export interface SingleWitnessCoreferenceChainV2ValidationIssue {
  readonly code: SingleWitnessCoreferenceChainV2ValidationIssueCode;
  readonly message: string;
  readonly path: readonly (string | number)[];
  readonly figureIndex?: number;
  readonly mentionIndex?: number;
  readonly unassignedMentionIndex?: number;
}

export type CoreferenceChainV2GroundingStatus = "grounded" | "ambiguous" | "not_found";

export type CoreferenceChainV2GroundingDiagnosticCode =
  | "empty-context"
  | "context-not-found"
  | "ambiguous-context"
  | "empty-mention"
  | "mention-not-found-in-context"
  | "ambiguous-mention";

export interface CoreferenceChainV2GroundingDiagnostic {
  readonly code: CoreferenceChainV2GroundingDiagnosticCode;
  readonly message: string;
  readonly path: readonly (string | number)[];
  readonly figureIndex?: number;
  readonly mentionIndex?: number;
  readonly unassignedMentionIndex?: number;
}

export interface GroundedSingleWitnessCoreferenceChainV2Mention
  extends SingleWitnessCoreferenceChainV2Mention {
  readonly status: CoreferenceChainV2GroundingStatus;
  readonly matchedText?: string;
  readonly contextRange?: CharacterMentionTextRange;
  readonly mentionRange?: CharacterMentionTextRange;
  readonly contextCandidateRanges: readonly CharacterMentionTextRange[];
  readonly mentionCandidateRanges: readonly CharacterMentionTextRange[];
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly sourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly CoreferenceChainV2GroundingDiagnostic[];
}

export interface GroundedSingleWitnessCoreferenceChainV2UnassignedMention
  extends SingleWitnessCoreferenceChainV2UnassignedMention {
  readonly status: CoreferenceChainV2GroundingStatus;
  readonly matchedText?: string;
  readonly contextRange?: CharacterMentionTextRange;
  readonly mentionRange?: CharacterMentionTextRange;
  readonly contextCandidateRanges: readonly CharacterMentionTextRange[];
  readonly mentionCandidateRanges: readonly CharacterMentionTextRange[];
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly sourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly CoreferenceChainV2GroundingDiagnostic[];
}

export interface GroundedSingleWitnessCoreferenceChainV2Figure {
  readonly id: string;
  readonly kind: CoreferenceChainV2FigureKind;
  readonly anchor: SingleWitnessCoreferenceChainV2Anchor;
  readonly status: CoreferenceChainV2GroundingStatus;
  readonly chain: readonly GroundedSingleWitnessCoreferenceChainV2Mention[];
  readonly diagnostics: readonly CoreferenceChainV2GroundingDiagnostic[];
  readonly [key: string]: unknown;
}

export interface GroundedSingleWitnessCoreferenceChainV2Output {
  readonly figures: readonly GroundedSingleWitnessCoreferenceChainV2Figure[];
  readonly unassigned_mentions: readonly GroundedSingleWitnessCoreferenceChainV2UnassignedMention[];
  readonly diagnostics: readonly CoreferenceChainV2GroundingDiagnostic[];
}

export interface GroundedSingleWitnessCoreferenceChainV2Window {
  readonly window: CoreferenceChainWindowMetadata;
  readonly validationIssues: readonly SingleWitnessCoreferenceChainV2ValidationIssue[];
  readonly preparedCallIssues: readonly PreparedCharacterExtractionValidationIssue[];
  readonly grounded: GroundedSingleWitnessCoreferenceChainV2Output;
  readonly diagnostics: readonly CoreferenceChainV2GroundingDiagnostic[];
}

export interface CoreferenceChainV2SourceAnchorProjection extends CharacterIndexSourceAnchor {
  readonly mentionType: CharacterIndexMentionType;
  readonly figureId: string;
  readonly mentionId: string;
}

export interface GroundSingleWitnessCoreferenceChainsV2Options {
  readonly maxCandidateRanges?: number;
}

interface GroundedCoreferenceChainV2MentionFields {
  readonly status: CoreferenceChainV2GroundingStatus;
  readonly matchedText?: string;
  readonly contextRange?: CharacterMentionTextRange;
  readonly mentionRange?: CharacterMentionTextRange;
  readonly contextCandidateRanges: readonly CharacterMentionTextRange[];
  readonly mentionCandidateRanges: readonly CharacterMentionTextRange[];
  readonly contextSourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly sourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly CoreferenceChainV2GroundingDiagnostic[];
}

export const buildSingleWitnessCoreferenceChainV2PromptText = (
  sourceText: string
): string => `${COREFERENCE_CHAIN_V2_PROMPT_PREFIX}\n${sourceText}`;

export const prepareSingleWitnessCoreferenceChainExtractionV2Call = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope,
  options: PrepareSingleWitnessCoreferenceChainV2ExtractionOptions = {}
): PreparedSingleWitnessCoreferenceChainV2ExtractionCall => {
  const sourceCall = prepareSingleWitnessCharacterExtractionCall(input, scope);
  const promptVersion =
    options.promptVersion || SINGLE_WITNESS_COREFERENCE_CHAIN_EXTRACTION_V2_PROMPT_VERSION;
  const promptTextBuilder =
    options.promptTextBuilder || buildSingleWitnessCoreferenceChainV2PromptText;

  return {
    promptText: promptTextBuilder(sourceCall.sourceText),
    sourceText: sourceCall.sourceText,
    sourceMap: sourceCall.sourceMap,
    scope: sourceCall.scope,
    promptVersion
  };
};

export const prepareSingleWitnessCoreferenceChainV2ExtractionWindowCalls = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope,
  options: PrepareSingleWitnessCoreferenceChainV2WindowOptions = {}
): PreparedSingleWitnessCoreferenceChainV2WindowCall[] => (
  prepareSingleWitnessCoreferenceChainExtractionWindowCalls(input, scope, {
    maxRowsPerWindow: options.maxRowsPerWindow,
    overlapRows: options.overlapRows,
    promptVersion: options.promptVersion || SINGLE_WITNESS_COREFERENCE_CHAIN_EXTRACTION_V2_PROMPT_VERSION,
    promptTextBuilder: options.promptTextBuilder || buildSingleWitnessCoreferenceChainV2PromptText
  }).map((windowCall) => ({
    promptText: windowCall.promptText,
    sourceText: windowCall.sourceText,
    sourceMap: windowCall.sourceMap,
    scope: windowCall.scope,
    promptVersion: windowCall.promptVersion,
    window: windowCall.window
  }))
);

export const validatePreparedCoreferenceChainV2ExtractionCall = (
  call: PreparedSingleWitnessCoreferenceChainV2ExtractionCall
): PreparedCharacterExtractionValidationIssue[] => validatePreparedExtractionCall(call);

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === "object" && value !== null && !Array.isArray(value)
);

const isNonEmptyString = (value: unknown): value is string => (
  typeof value === "string" && value.trim().length > 0
);

const figureKinds = new Set<CoreferenceChainV2FigureKind>(["person", "animal", "being", "group"]);

const stringArrayIsValid = (value: unknown): value is readonly string[] => (
  Array.isArray(value) && value.every((entry) => typeof entry === "string" && entry.trim().length > 0)
);

const pushIssue = (
  issues: SingleWitnessCoreferenceChainV2ValidationIssue[],
  issue: SingleWitnessCoreferenceChainV2ValidationIssue
): void => {
  issues.push(issue);
};

const validateAnchor = (
  value: unknown,
  issues: SingleWitnessCoreferenceChainV2ValidationIssue[],
  path: readonly (string | number)[],
  figureIndex: number
): void => {
  if (!isRecord(value)) {
    pushIssue(issues, {
      code: "invalid-anchor",
      message: "Coreference v2 figure anchor must be an object.",
      path,
      figureIndex
    });
    return;
  }

  if (!isNonEmptyString(value.name)) {
    pushIssue(issues, {
      code: "empty-anchor-name",
      message: "Coreference v2 anchor must include a non-empty name string.",
      path: [...path, "name"],
      figureIndex
    });
  }

  if (!isNonEmptyString(value.transliteration)) {
    pushIssue(issues, {
      code: "empty-anchor-transliteration",
      message: "Coreference v2 anchor must include a non-empty transliteration string.",
      path: [...path, "transliteration"],
      figureIndex
    });
  }
};

const validateMention = (
  value: unknown,
  issues: SingleWitnessCoreferenceChainV2ValidationIssue[],
  path: readonly (string | number)[],
  figureIndex: number,
  mentionIndex: number
): void => {
  if (!isRecord(value)) {
    pushIssue(issues, {
      code: "invalid-chain-mention",
      message: "Coreference v2 chain mention must be an object.",
      path,
      figureIndex,
      mentionIndex
    });
    return;
  }

  if (!isNonEmptyString(value.id)) {
    pushIssue(issues, {
      code: "empty-chain-mention-id",
      message: "Coreference v2 chain mention must include a non-empty id string.",
      path: [...path, "id"],
      figureIndex,
      mentionIndex
    });
  }

  if (!isNonEmptyString(value.mention)) {
    pushIssue(issues, {
      code: "empty-mention",
      message: "Coreference v2 chain mention must include a non-empty mention string.",
      path: [...path, "mention"],
      figureIndex,
      mentionIndex
    });
  }

  if (!isNonEmptyString(value.context)) {
    pushIssue(issues, {
      code: "empty-context",
      message: "Coreference v2 chain mention must include a non-empty context string.",
      path: [...path, "context"],
      figureIndex,
      mentionIndex
    });
  }

  if (value.with !== undefined && !stringArrayIsValid(value.with)) {
    pushIssue(issues, {
      code: "invalid-with",
      message: "Coreference v2 chain mention with must be an array of non-empty figure ids when present.",
      path: [...path, "with"],
      figureIndex,
      mentionIndex
    });
  }
};

const validateUnassignedMention = (
  value: unknown,
  issues: SingleWitnessCoreferenceChainV2ValidationIssue[],
  path: readonly (string | number)[],
  unassignedMentionIndex: number
): void => {
  if (!isRecord(value)) {
    pushIssue(issues, {
      code: "invalid-unassigned-mention",
      message: "Coreference v2 unassigned mention must be an object.",
      path,
      unassignedMentionIndex
    });
    return;
  }

  if (!isNonEmptyString(value.mention)) {
    pushIssue(issues, {
      code: "empty-mention",
      message: "Coreference v2 unassigned mention must include a non-empty mention string.",
      path: [...path, "mention"],
      unassignedMentionIndex
    });
  }

  if (!isNonEmptyString(value.context)) {
    pushIssue(issues, {
      code: "empty-context",
      message: "Coreference v2 unassigned mention must include a non-empty context string.",
      path: [...path, "context"],
      unassignedMentionIndex
    });
  }

  if (value.candidates !== undefined && !stringArrayIsValid(value.candidates)) {
    pushIssue(issues, {
      code: "invalid-candidates",
      message: "Coreference v2 unassigned mention candidates must be an array of non-empty figure ids when present.",
      path: [...path, "candidates"],
      unassignedMentionIndex
    });
  }
};

export const validateSingleWitnessCoreferenceChainV2Output = (
  output: unknown
): SingleWitnessCoreferenceChainV2ValidationIssue[] => {
  const issues: SingleWitnessCoreferenceChainV2ValidationIssue[] = [];

  if (!isRecord(output)) {
    pushIssue(issues, {
      code: "invalid-output",
      message: "Coreference v2 output must be an object.",
      path: []
    });
    return issues;
  }

  if (!Array.isArray(output.figures)) {
    pushIssue(issues, {
      code: "invalid-figures",
      message: "Coreference v2 output must include a figures array.",
      path: ["figures"]
    });
    return issues;
  }

  output.figures.forEach((figure, figureIndex) => {
    const figurePath = ["figures", figureIndex] as const;
    if (!isRecord(figure)) {
      pushIssue(issues, {
        code: "invalid-figure",
        message: "Coreference v2 figure must be an object.",
        path: figurePath,
        figureIndex
      });
      return;
    }

    if (!isNonEmptyString(figure.id)) {
      pushIssue(issues, {
        code: "empty-figure-id",
        message: "Coreference v2 figure must include a non-empty id string.",
        path: [...figurePath, "id"],
        figureIndex
      });
    }

    if (typeof figure.kind !== "string" || !figureKinds.has(figure.kind as CoreferenceChainV2FigureKind)) {
      pushIssue(issues, {
        code: "invalid-kind",
        message: "Coreference v2 figure kind must be one of person, animal, being, group.",
        path: [...figurePath, "kind"],
        figureIndex
      });
    }

    validateAnchor(figure.anchor, issues, [...figurePath, "anchor"], figureIndex);

    if (!Array.isArray(figure.chain)) {
      pushIssue(issues, {
        code: "invalid-chain",
        message: "Coreference v2 figure must include a chain array.",
        path: [...figurePath, "chain"],
        figureIndex
      });
      return;
    }

    figure.chain.forEach((mention, mentionIndex) => {
      validateMention(
        mention,
        issues,
        [...figurePath, "chain", mentionIndex],
        figureIndex,
        mentionIndex
      );
    });
  });

  if (!Array.isArray(output.unassigned_mentions)) {
    pushIssue(issues, {
      code: "invalid-unassigned-mentions",
      message: "Coreference v2 output must include an unassigned_mentions array.",
      path: ["unassigned_mentions"]
    });
    return issues;
  }

  output.unassigned_mentions.forEach((mention, unassignedMentionIndex) => {
    validateUnassignedMention(
      mention,
      issues,
      ["unassigned_mentions", unassignedMentionIndex],
      unassignedMentionIndex
    );
  });

  return issues;
};

const findExactRanges = (
  sourceText: string,
  text: string
): CharacterMentionTextRange[] => {
  if (!text) {
    return [];
  }

  const ranges: CharacterMentionTextRange[] = [];
  let cursor = 0;
  while (cursor <= sourceText.length) {
    const start = sourceText.indexOf(text, cursor);
    if (start < 0) {
      break;
    }
    const end = start + text.length;
    ranges.push({ start, end });
    cursor = start + 1;
  }
  return ranges;
};

const limitCandidateRanges = (
  ranges: readonly CharacterMentionTextRange[],
  options: GroundSingleWitnessCoreferenceChainsV2Options
): readonly CharacterMentionTextRange[] => {
  if (typeof options.maxCandidateRanges !== "number") {
    return ranges;
  }
  return ranges.slice(0, Math.max(0, Math.floor(options.maxCandidateRanges)));
};

const toAbsoluteRanges = (
  contextRange: CharacterMentionTextRange,
  relativeRanges: readonly CharacterMentionTextRange[]
): CharacterMentionTextRange[] => (
  relativeRanges.map((range) => ({
    start: contextRange.start + range.start,
    end: contextRange.start + range.end
  }))
);

const diagnostic = (
  code: CoreferenceChainV2GroundingDiagnosticCode,
  message: string,
  path: readonly (string | number)[],
  figureIndex?: number,
  mentionIndex?: number,
  unassignedMentionIndex?: number
): CoreferenceChainV2GroundingDiagnostic => ({
  code,
  message,
  path,
  ...(figureIndex !== undefined ? { figureIndex } : {}),
  ...(mentionIndex !== undefined ? { mentionIndex } : {}),
  ...(unassignedMentionIndex !== undefined ? { unassignedMentionIndex } : {})
});

const groundMentionText = (
  call: Pick<PreparedSingleWitnessCoreferenceChainV2ExtractionCall, "sourceText" | "sourceMap">,
  mention: Pick<SingleWitnessCoreferenceChainV2Mention, "mention" | "context">,
  path: readonly (string | number)[],
  options: GroundSingleWitnessCoreferenceChainsV2Options,
  figureIndex?: number,
  mentionIndex?: number,
  unassignedMentionIndex?: number
): GroundedCoreferenceChainV2MentionFields => {
  if (mention.context.trim().length === 0) {
    return {
      status: "not_found",
      contextCandidateRanges: [],
      mentionCandidateRanges: [],
      contextSourceAnchors: [],
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "empty-context",
          "Coreference v2 mention context is empty.",
          path,
          figureIndex,
          mentionIndex,
          unassignedMentionIndex
        )
      ]
    };
  }

  const contextRanges = findExactRanges(call.sourceText, mention.context);
  const contextCandidateRanges = limitCandidateRanges(contextRanges, options);
  if (contextRanges.length === 0) {
    return {
      status: "not_found",
      contextCandidateRanges,
      mentionCandidateRanges: [],
      contextSourceAnchors: [],
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "context-not-found",
          "Coreference v2 mention context does not occur in the prepared sourceText.",
          path,
          figureIndex,
          mentionIndex,
          unassignedMentionIndex
        )
      ]
    };
  }

  if (contextRanges.length > 1) {
    return {
      status: "ambiguous",
      contextCandidateRanges,
      mentionCandidateRanges: [],
      contextSourceAnchors: [],
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "ambiguous-context",
          "Coreference v2 mention context occurs more than once in the prepared sourceText.",
          path,
          figureIndex,
          mentionIndex,
          unassignedMentionIndex
        )
      ]
    };
  }

  const contextRange = contextRanges[0]!;
  const contextText = call.sourceText.slice(contextRange.start, contextRange.end);
  const contextSourceAnchors = mapValidatedWindowRangeToSourceAnchors(call, contextRange);

  if (mention.mention.trim().length === 0) {
    return {
      status: "not_found",
      contextRange,
      contextCandidateRanges,
      mentionCandidateRanges: [],
      contextSourceAnchors,
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "empty-mention",
          "Coreference v2 mention text is empty.",
          path,
          figureIndex,
          mentionIndex,
          unassignedMentionIndex
        )
      ]
    };
  }

  const relativeMentionRanges = findExactRanges(contextText, mention.mention);
  const mentionRanges = toAbsoluteRanges(contextRange, relativeMentionRanges);
  const mentionCandidateRanges = limitCandidateRanges(mentionRanges, options);
  if (mentionRanges.length === 0) {
    return {
      status: "not_found",
      contextRange,
      contextCandidateRanges,
      mentionCandidateRanges,
      contextSourceAnchors,
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "mention-not-found-in-context",
          "Coreference v2 mention does not occur inside its grounded context.",
          path,
          figureIndex,
          mentionIndex,
          unassignedMentionIndex
        )
      ]
    };
  }

  if (mentionRanges.length > 1) {
    return {
      status: "ambiguous",
      contextRange,
      contextCandidateRanges,
      mentionCandidateRanges,
      contextSourceAnchors,
      sourceAnchors: [],
      diagnostics: [
        diagnostic(
          "ambiguous-mention",
          "Coreference v2 mention occurs more than once inside its grounded context.",
          path,
          figureIndex,
          mentionIndex,
          unassignedMentionIndex
        )
      ]
    };
  }

  const mentionRange = mentionRanges[0]!;
  return {
    status: "grounded",
    matchedText: mention.mention,
    contextRange,
    mentionRange,
    contextCandidateRanges,
    mentionCandidateRanges,
    contextSourceAnchors,
    sourceAnchors: mapValidatedWindowRangeToSourceAnchors(call, mentionRange),
    diagnostics: []
  };
};

const combineStatuses = (
  statuses: readonly CoreferenceChainV2GroundingStatus[]
): CoreferenceChainV2GroundingStatus => {
  if (statuses.some((status) => status === "not_found")) {
    return "not_found";
  }
  if (statuses.some((status) => status === "ambiguous")) {
    return "ambiguous";
  }
  return "grounded";
};

export const groundSingleWitnessCoreferenceChainsV2 = (
  call: Pick<PreparedSingleWitnessCoreferenceChainV2ExtractionCall, "sourceText" | "sourceMap">,
  output: SingleWitnessCoreferenceChainV2Output,
  options: GroundSingleWitnessCoreferenceChainsV2Options = {}
): GroundedSingleWitnessCoreferenceChainV2Output => {
  const figures = output.figures.map((figure, figureIndex) => {
    const groundedChain = figure.chain.map((mention, mentionIndex) => ({
      ...mention,
      ...groundMentionText(
        call,
        mention,
        ["figures", figureIndex, "chain", mentionIndex],
        options,
        figureIndex,
        mentionIndex
      )
    }));
    const diagnostics = groundedChain.flatMap((mention) => mention.diagnostics);

    return {
      ...figure,
      status: combineStatuses(groundedChain.map((mention) => mention.status)),
      chain: groundedChain,
      diagnostics
    };
  });

  const unassigned_mentions = output.unassigned_mentions.map((mention, unassignedMentionIndex) => ({
    ...mention,
    ...groundMentionText(
      call,
      mention,
      ["unassigned_mentions", unassignedMentionIndex],
      options,
      undefined,
      undefined,
      unassignedMentionIndex
    )
  }));

  return {
    figures,
    unassigned_mentions,
    diagnostics: [
      ...figures.flatMap((figure) => figure.diagnostics),
      ...unassigned_mentions.flatMap((mention) => mention.diagnostics)
    ]
  };
};

const isGroundedAnchorMention = (
  figure: GroundedSingleWitnessCoreferenceChainV2Figure,
  mention: GroundedSingleWitnessCoreferenceChainV2Mention
): boolean => (
  mention.status === "grounded" &&
  (mention.mention === figure.anchor.name || mention.matchedText === figure.anchor.name)
);

export const projectGroundedCoreferenceChainsV2ToSourceAnchors = (
  grounded: GroundedSingleWitnessCoreferenceChainV2Output
): CoreferenceChainV2SourceAnchorProjection[] => (
  grounded.figures.flatMap((figure) => {
    let projectedAnchorMention = false;

    return figure.chain.flatMap((mention) => {
      if (mention.status !== "grounded" || mention.sourceAnchors.length === 0) {
        return [];
      }

      const mentionType: CharacterIndexMentionType =
        !projectedAnchorMention && isGroundedAnchorMention(figure, mention)
          ? "name"
          : "coref";
      if (mentionType === "name") {
        projectedAnchorMention = true;
      }

      return mention.sourceAnchors.map((sourceAnchor) => ({
        ...sourceAnchor,
        mentionType,
        figureId: figure.id,
        mentionId: mention.id
      }));
    });
  })
);

const isSingleWitnessCoreferenceChainV2Output = (
  output: unknown
): output is SingleWitnessCoreferenceChainV2Output => (
  validateSingleWitnessCoreferenceChainV2Output(output).length === 0
);

export const groundSingleWitnessCoreferenceChainV2WindowResponse = (
  call: PreparedSingleWitnessCoreferenceChainV2WindowCall,
  output: unknown,
  options: GroundSingleWitnessCoreferenceChainsV2Options = {}
): GroundedSingleWitnessCoreferenceChainV2Window => {
  const validationIssues = validateSingleWitnessCoreferenceChainV2Output(output);
  const preparedCallIssues = validatePreparedCoreferenceChainV2ExtractionCall(call);
  const grounded = validationIssues.length === 0 && isSingleWitnessCoreferenceChainV2Output(output)
    ? groundSingleWitnessCoreferenceChainsV2(call, output, options)
    : { figures: [], unassigned_mentions: [], diagnostics: [] };

  return {
    window: call.window,
    validationIssues,
    preparedCallIssues,
    grounded,
    diagnostics: grounded.diagnostics
  };
};
